import { ComponentProps, Fragment } from 'react';

import { Tooltip } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import PersonIcon from '@material-ui/icons/Person';

import {
  RiCustomerService2Line,
  RiSparklingLine,
  RiVideoLine,
} from '@remixicon/react';

import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  useSidebarPinState,
} from '@backstage/core-components';
import { IconComponent } from '@backstage/frontend-plugin-api';

import {
  IconDeviceComputer,
  IconExclamationCircle,
  ModuleIcons,
  getPageDescriptor,
  useAuth,
  useAuthFactory,
  useFeatureFlag,
  useLocation,
  useSupportMessenger,
} from '@spotify/confidence-core-react';
import { useAccountMode } from '@spotify/confidence-graphql';
import { adminRouteRef } from '@spotify/plugin-confidence-admin';
import { useCheckResourcePermissions } from '@spotify/plugin-confidence-permissions-react';

import { ProfileMenu } from '../ProfileMenu';
import { getMenuLinks, isMenuItem } from './MenuLinks';

const TooltipSidebarItem = ({
  text = '',
  ...props
}: ComponentProps<typeof SidebarItem>) => {
  const { isPinned } = useSidebarPinState();
  return (
    <Tooltip title={isPinned ? '' : text} placement="right" arrow>
      <SidebarItem text={text} {...props} />
    </Tooltip>
  );
};

const NO_SIDEBAR_PAGES = ['/invitation', '/onboarding', '/signup', '/beta'];

const adminRootPage = getPageDescriptor(adminRouteRef);

const SupportButton = ({ onClick }: { onClick: () => void }) => {
  const { isPinned } = useSidebarPinState();
  return (
    <Tooltip title={isPinned ? '' : 'Support'} placement="right" arrow>
      <SidebarItem
        text="Support"
        icon={RiCustomerService2Line as IconComponent}
        onClick={onClick}
      />
    </Tooltip>
  );
};

export const SidebarMenu = () => {
  const { user, isAuthenticated } = useAuth();
  const { overrideToken } = useAuthFactory();
  const { isTotalConfidence } = useAccountMode();
  const showEvents = useFeatureFlag('show-events.enabled', false);
  const showInitiatives = useFeatureFlag('initiatives.enabled', false);
  const showExplore = useFeatureFlag('insights-agent.enabled', false);
  const showRecordings = useFeatureFlag('session-replay.enabled', false);
  const isDebugtabEnabled = useFeatureFlag('debugtab.enabled', false);
  const isMeasurementsEnabled = useFeatureFlag('measurements.enabled', false);
  const showMeasurements = isDebugtabEnabled && isMeasurementsEnabled;
  const { openChat } = useSupportMessenger();

  const { allowed: canViewInsights } = useCheckResourcePermissions({
    can: 'read',
    name: 'dashboards/global',
  });

  const location = useLocation();
  const pageWithoutSidebar = NO_SIDEBAR_PAGES.some(page =>
    location.pathname.startsWith(page),
  );

  if (!isAuthenticated || pageWithoutSidebar) {
    return null;
  }

  return (
    <Sidebar disableExpandOnHover>
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          {getMenuLinks({
            isTotalConfidence,
            showEvents,
            showInitiatives,
            showMeasurements,
          }).map((m, i) => {
            if (isMenuItem(m)) {
              let targetPath = m.to;
              // Highlight Segments when on materialized segments routes
              const isMaterializedSegmentsActive = location.pathname.startsWith(
                '/materializedSegments',
              );
              if (m.to === '/segments' && isMaterializedSegmentsActive) {
                targetPath = location.pathname;
              }

              return (
                <TooltipSidebarItem
                  key={m.to}
                  icon={m.icon ?? ModuleIcons.abtests}
                  to={targetPath}
                  text={m.label}
                />
              );
            }
            return <Fragment key={`menu-item-${i}`}>{m}</Fragment>;
          })}

          {showRecordings && (
            <>
              <SidebarDivider />
              <TooltipSidebarItem
                icon={RiVideoLine as IconComponent}
                to="/recordings/explorer"
                text="Recordings"
              />
            </>
          )}

          {canViewInsights && (
            <>
              <SidebarDivider />
              <TooltipSidebarItem
                icon={ModuleIcons.insights}
                to="/insights"
                text="Insights"
              />
              <SidebarDivider />
            </>
          )}

          {showExplore && (
            <TooltipSidebarItem
              icon={RiSparklingLine as IconComponent}
              to="/explore"
              text="Explore"
            />
          )}
        </div>
      </SidebarGroup>
      <SidebarGroup label="Settings" icon={<PersonIcon />}>
        <TooltipSidebarItem
          text={adminRootPage.title}
          to="/admin"
          icon={IconDeviceComputer}
        />
        {(user?.email?.endsWith('@spotify.com') || overrideToken) && (
          <TooltipSidebarItem
            text="Debug"
            to="/debug"
            icon={IconExclamationCircle}
          />
        )}
        <SupportButton
          onClick={() =>
            openChat(
              user?.name && user?.email
                ? { name: user.name, email: user.email }
                : undefined,
            )
          }
        />
        <SidebarDivider />
        <ProfileMenu />
      </SidebarGroup>
    </Sidebar>
  );
};
